import type { MapFilterState } from './mapFilters';

// The legacy feature-filter pipeline (filterPoisByTypes / applyCommittedFilters and their
// per-dimension helpers) is gone with the pre-split POI layer: the entity map filters
// through applyEntityFilters, and the table/stats through serialiseEntityFiltersToParams.
// What remains here is the one dimension both eras share: turning a date-range option
// into concrete inclusive bounds.

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

function lastDayOfMonthIso(year: number, month0: number): string {
    // Day 0 of the next month = last day of this month.
    const lastDay = new Date(year, month0 + 1, 0).getDate();
    return `${String(year)}-${pad2(month0 + 1)}-${pad2(lastDay)}`;
}

/**
 * Computes inclusive ISO "YYYY-MM-DD" bounds for the active date range option.
 * All (the default) and Custom with neither date → { from: null, to: null }, meaning no
 * date restriction.
 */
export function getDateRangeBounds(
    filters: Pick<MapFilterState, 'dateRange' | 'customDateRange'>,
    now = new Date(),
): { from: string | null; to: string | null } {
    const year = now.getFullYear();
    const month0 = now.getMonth();

    switch (filters.dateRange) {
        case 'all':
            return { from: null, to: null };
        case 'thisMonth':
            return {
                from: `${String(year)}-${pad2(month0 + 1)}-01`,
                to: lastDayOfMonthIso(year, month0),
            };
        case 'thisQuarter': {
            const quarterStartMonth0 = Math.floor(month0 / 3) * 3;
            const quarterEndMonth0 = quarterStartMonth0 + 2;
            return {
                from: `${String(year)}-${pad2(quarterStartMonth0 + 1)}-01`,
                to: lastDayOfMonthIso(year, quarterEndMonth0),
            };
        }
        case 'thisYear':
            return { from: `${String(year)}-01-01`, to: `${String(year)}-12-31` };
        case 'custom':
            return { from: filters.customDateRange.from, to: filters.customDateRange.to };
    }
}
